/**
 * Legacy Unity `/account/*` URLs → canonical `/settings/*` targets.
 *
 * These paths predate the move of account administration under `/settings`.
 * Hydra forwards them natively (see `account.$.tsx`), so the embedded app never
 * has to boot just to bounce the URL.
 *
 * `resolveAccountRedirect` returns `null` for unknown paths, and those fall
 * through to the embedded Journeys app.
 */

import { redirect } from '@tanstack/react-router';

export type AccountRedirect =
  | { kind: 'settings'; to: string }
  | { kind: 'workspace-settings'; workspaceId: string; page: 'edit' | 'confirm_delete' }
  /** An account confirmation email link. The confirmation page reads the code as `confirmationCode`. */
  | { kind: 'confirm-account'; confirmationCode: string | null }
  /** A receipt is server-rendered from Stripe, not an app route, so it needs a full page load. */
  | { kind: 'receipt'; href: string };

// Exact splat → target. The splat is the portion after `/account/`.
const STATIC_ACCOUNT_REDIRECTS: Record<string, string> = {
  '': '/settings/account',
  edit: '/settings/account/edit',
  subscription: '/settings/subscription',
  'subscription/new': '/settings/subscription',
  security: '/settings/personal/security',
  'security/new': '/settings/personal/security/new',
  'security/backup_codes': '/settings/personal/security/backup_codes',
  api_credentials: '/settings/api_credentials',
  // `/account/api_credentials/new` historically dropped the `/new` segment.
  'api_credentials/new': '/settings/api_credentials',
  receipts: '/settings/receipts',
  team: '/settings/team',
  workspaces: '/settings/workspaces',
  'workspaces/new': '/settings/workspaces/new',
};

// `/account/team/:id/passwords/edit` → `/settings/team/set_password`
// (the user id is intentionally dropped, matching the Ember redirect).
const TEAM_PASSWORD_EDIT = /^team\/[^/]+\/passwords\/edit$/;

// `/account/workspaces/:id/(edit|confirm_delete)` → that workspace's settings page.
const WORKSPACE_SETTINGS = /^workspaces\/([^/]+)\/(edit|confirm_delete)$/;

// `/account/receipts/:id` → the receipt, which the server renders from Stripe.
const RECEIPT = /^receipts\/([^/]+)$/;

// `/account/api_credentials/:id/confirm_delete` → the API credentials list. Deleting a key is a
// modal on that list now; there is no confirmation page left to land on.
const API_CREDENTIAL_CONFIRM_DELETE = /^api_credentials\/[^/]+\/confirm_delete$/;

/** `search` is the query string, with or without its leading `?`. */
export function resolveAccountRedirect(splat: string, search = ''): AccountRedirect | null {
  const normalized = splat.replace(/\/+$/, '');

  if (normalized === 'confirm') {
    return { kind: 'confirm-account', confirmationCode: new URLSearchParams(search).get('confirmation_code') };
  }

  if (Object.prototype.hasOwnProperty.call(STATIC_ACCOUNT_REDIRECTS, normalized)) {
    return { kind: 'settings', to: STATIC_ACCOUNT_REDIRECTS[normalized] };
  }

  if (TEAM_PASSWORD_EDIT.test(normalized)) {
    return { kind: 'settings', to: '/settings/team/set_password' };
  }

  if (API_CREDENTIAL_CONFIRM_DELETE.test(normalized)) {
    return { kind: 'settings', to: '/settings/api_credentials' };
  }

  const workspaceSettings = WORKSPACE_SETTINGS.exec(normalized);
  if (workspaceSettings != null) {
    const [, workspaceId, page] = workspaceSettings;
    return { kind: 'workspace-settings', workspaceId, page: page === 'edit' ? 'edit' : 'confirm_delete' };
  }

  const receipt = RECEIPT.exec(normalized);
  if (receipt != null) {
    return { kind: 'receipt', href: `/settings/receipts/${receipt[1]}` };
  }

  return null;
}

/** The router redirect `account.$` throws for a resolved target. */
export function accountRouterRedirect(target: AccountRedirect) {
  switch (target.kind) {
    case 'settings':
      return redirect({ to: target.to });
    case 'workspace-settings':
      return target.page === 'edit'
        ? redirect({ to: '/workspaces/$workspaceId/settings/edit', params: { workspaceId: target.workspaceId } })
        : redirect({
            to: '/workspaces/$workspaceId/settings/confirm_delete',
            params: { workspaceId: target.workspaceId },
          });
    case 'confirm-account': {
      // A full page load, so the code reaches the confirmation page exactly as written. A router
      // navigation would parse it as JSON and write it back out (`12e45` becomes `1.2e+46`).
      const query =
        target.confirmationCode == null ? '' : `?${new URLSearchParams({ confirmationCode: target.confirmationCode })}`;
      return redirect({ href: `/confirm_account${query}`, reloadDocument: true, replace: true });
    }
    case 'receipt':
      return redirect({ href: target.href, reloadDocument: true, replace: true });
    default: {
      const _exhaustive: never = target;
      throw new Error(`Unhandled account redirect: ${String(_exhaustive)}`);
    }
  }
}
